import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input } from '@angular/core';
import { ChartLoader } from '@cfp/shared/ui/chart';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartDataset, ChartOptions, LegendItem } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { LloydsDecarbonisationPathwayDTO, LloydsDecarbonisationPathwayYearDTO } from 'csr-asset-app';

@Component({
  selector: 'cfp-lbg-decarbonisation-pathway-chart',
  templateUrl: 'lbg-decarbonisation-pathway-chart.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BaseChartDirective, TranslateModule],
  providers: [DecimalPipe, DatePipe]
})
export class LbgDecarbonisationPathwayChart implements OnInit {
  public readonly pathway = input.required<LloydsDecarbonisationPathwayDTO>();
  public readonly yAxisTitleKey = input<string>('lbgPortfolioRoadmapReport.decarbonisationPathway.yAxis.average');

  private readonly translateService = inject(TranslateService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly datePipe = inject(DatePipe);
  private readonly chartLoader = inject(ChartLoader);

  private readonly style = getComputedStyle(document.body);
  private readonly pathwayColor =
    this.style.getPropertyValue('--chart-pathway').trim() || this.style.getPropertyValue('--cfp-primary').trim();
  private readonly withMeasuresColor =
    this.style.getPropertyValue('--chart-pathway-with-measures').trim() ||
    this.style.getPropertyValue('--cfp-black').trim();
  private readonly withoutMeasuresColor =
    this.style.getPropertyValue('--chart-pathway-without-measure').trim() || this.withMeasuresColor;
  private readonly misalignmentColor =
    this.style.getPropertyValue('--chart-misalignment').trim() || this.style.getPropertyValue('--cfp-danger').trim();
  private shouldDisableChartAnimation = false;

  public ngOnInit(): void {
    this.chartLoader.load();
  }

  protected readonly chartOptions = computed<ChartOptions>(() => {
    const years = this.pathway().years;
    const minYear = years?.length > 0 ? years[0].year : undefined;
    const maxYear = years?.length > 0 ? years[years.length - 1].year : undefined;

    return {
      responsive: true,
      resizeDelay: 200,
      maintainAspectRatio: true,
      // Figma 1381:10471 box is 1248×438
      aspectRatio: 1248 / 438,
      animation: this.shouldDisableChartAnimation ? false : undefined,
      layout: {
        padding: {
          bottom: 8,
          right: 24
        }
      },
      scales: {
        x: {
          type: 'category',
          offset: false,
          grid: {
            display: false,
            drawTicks: false
          },
          ticks: {
            autoSkip: false,
            padding: 10,
            callback: (_tickValue, index) => this.getXTickLabels(years[index], index)
          }
        },
        xScatter: {
          type: 'linear',
          offset: false,
          min: minYear,
          max: maxYear,
          display: false
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: this.translateService.instant(this.yAxisTitleKey())
          }
        }
      },
      plugins: {
        datalabels: { display: () => false },
        tooltip: {
          callbacks: {
            labelColor: (tooltipItem) => {
              return {
                borderColor: tooltipItem.dataset.borderColor as string,
                backgroundColor: tooltipItem.dataset.backgroundColor as string,
                borderWidth: tooltipItem.dataset.borderWidth as number,
                borderDash: tooltipItem.dataset.borderDash as [number, number]
              };
            },
            label: (tooltipItem) => {
              const value = this.decimalPipe.transform(tooltipItem.parsed.y, '1.0-0');
              return `${value} kg/m²`;
            },
            title: (tooltipItems) => {
              const titles = tooltipItems
                .map((tooltipItem) => {
                  if (tooltipItem.dataset.type === 'scatter') {
                    const year = Math.floor(tooltipItem.parsed.x);
                    const monthIndex = Math.floor((tooltipItem.parsed.x - year) * 12);
                    return this.datePipe.transform(new Date(year, monthIndex), 'MMMM yyyy') ?? '';
                  }
                  return tooltipItem.label != null ? String(tooltipItem.label) : '';
                })
                .filter((title): title is string => title.length > 0);
              return [...new Set(titles)];
            }
          }
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            padding: 16,
            usePointStyle: true,
            generateLabels: () => this.createLegendItems()
          }
        }
      }
    };
  });

  protected readonly chartData = computed(() => {
    const pathway = this.pathway();
    const years = pathway.years;
    if (!years?.length) {
      return undefined;
    }

    const datasets: ChartDataset[] = [
      {
        type: 'line',
        label: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.lloydsPathway'),
        data: years.map((year) => year.lloydsPathwayIntensityKgM2),
        borderColor: this.pathwayColor,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: this.pathwayColor,
        pointHoverBackgroundColor: this.pathwayColor,
        borderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        tension: 0.35
      },
      {
        type: 'line',
        label: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.withMeasures'),
        data: years.map((year) => year.withMeasuresIntensityKgM2),
        borderColor: this.withMeasuresColor,
        backgroundColor: this.withMeasuresColor,
        pointHoverBorderColor: this.withMeasuresColor,
        pointHoverBackgroundColor: this.withMeasuresColor,
        borderWidth: 2,
        pointRadius: years.map((_, index) => (index === 0 ? 5 : 0)),
        pointStyle: 'rectRot',
        pointHitRadius: 10,
        tension: 0
      },
      {
        type: 'line',
        label: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.withoutMeasures'),
        data: years.map((year) => year.withoutMeasuresIntensityKgM2),
        borderColor: this.withoutMeasuresColor,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: this.withoutMeasuresColor,
        pointHoverBackgroundColor: this.withoutMeasuresColor,
        borderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        borderDash: [3, 3],
        tension: 0
      }
    ];

    const strandedInterceptionPoints = pathway.strandedInterceptionPoints;
    if (strandedInterceptionPoints?.length) {
      datasets.push({
        type: 'scatter',
        label: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.misalignment'),
        data: strandedInterceptionPoints.map((point) => ({
          x: point.years,
          y: point.intensityKgM2 ?? 0
        })),
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderColor: this.misalignmentColor,
        pointRadius: 10,
        borderWidth: 2,
        pointHoverRadius: 10,
        pointHoverBorderWidth: 2,
        pointStyle: 'circle',
        xAxisID: 'xScatter'
      });
    }

    const chartData: ChartData = {
      datasets,
      labels: years.map((year) => String(year.year))
    };

    this.shouldDisableChartAnimation = true;
    return chartData;
  });

  private createLegendItems(): LegendItem[] {
    return [
      {
        text: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.lloydsPathway'),
        lineWidth: 2,
        strokeStyle: this.pathwayColor,
        fillStyle: 'rgba(0, 0, 0, 0)',
        pointStyle: 'line'
      },
      {
        text: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.withMeasures'),
        lineWidth: 2,
        strokeStyle: this.withMeasuresColor,
        fillStyle: 'rgba(0, 0, 0, 0)',
        pointStyle: 'line'
      },
      {
        text: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.misalignment'),
        lineWidth: 2,
        strokeStyle: this.misalignmentColor,
        fillStyle: 'rgba(0, 0, 0, 0)',
        pointStyle: 'circle'
      },
      {
        text: this.translateService.instant('lbgPortfolioRoadmapReport.decarbonisationPathway.legend.withoutMeasures'),
        lineWidth: 2,
        strokeStyle: this.withoutMeasuresColor,
        fillStyle: 'rgba(0, 0, 0, 0)',
        pointStyle: 'line',
        lineDash: [3, 3]
      }
    ];
  }

  private getXTickLabels(year: LloydsDecarbonisationPathwayYearDTO | undefined, index: number): string | string[] {
    if (!year) {
      return '';
    }

    const intensity = this.decimalPipe.transform(year.withMeasuresIntensityKgM2, '1.0-0');
    const intensityLabel = `${intensity} kg/m²`;

    if (index === 0) {
      return [String(year.year), intensityLabel];
    }

    const percentage = this.decimalPipe.transform(year.yearOnYearReductionPercentage, '1.0-0');
    return [String(year.year), intensityLabel, `(${percentage}%)`];
  }
}
